import PortalLayout from "@/components/PortalLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Download, Search, ChevronDown, ChevronRight, Clock } from "lucide-react";
import { useState } from "react";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Documents = () => {
  const { companyId } = usePortalCompany();
  const [search, setSearch] = useState("");
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("documents")
        .select("*")
        .eq("company_id", companyId!)
        .eq("is_archived", false)
        .order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!companyId,
  });

  const filterByCategory = (category: string) => {
    const catDocs = documents.filter((d: any) => (d.category || "project_documents") === category);
    const parentDocs = catDocs.filter((d) => !d.parent_document_id);
    const getVersions = (parentId: string) =>
      catDocs
        .filter((d) => d.parent_document_id === parentId)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const filtered = parentDocs.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      (d.description ?? "").toLowerCase().includes(search.toLowerCase())
    );

    return { filtered, getVersions };
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    const { data } = await supabase.storage
      .from("client-documents")
      .createSignedUrl(filePath, 60);
    if (data?.signedUrl) {
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = fileName;
      a.click();
    }
  };

  const renderDocList = (category: string) => {
    const { filtered, getVersions } = filterByCategory(category);

    if (isLoading) {
      return (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <div className="glass-card rounded-2xl p-16 text-center">
          <FileText className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">
            {search ? "No documents match your search." : "No documents available yet."}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {filtered.map((doc) => {
          const versions = getVersions(doc.id);
          const isExpanded = expandedDoc === doc.id;

          return (
            <div key={doc.id}>
              <div className="glass-card rounded-xl p-5 flex items-center gap-4 hover:border-teal/20 transition-all group">
                {versions.length > 0 && (
                  <button
                    onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                    className="p-1 text-primary-foreground/30 hover:text-primary-foreground/60 transition-colors"
                  >
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                )}
                <div className="w-11 h-11 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-teal" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-primary-foreground/80 font-body font-medium truncate">
                    {doc.name}
                    {doc.version && (
                      <span className="ml-2 px-2 py-0.5 rounded-md bg-teal/10 text-teal text-[10px] font-bold align-middle">
                        v{doc.version}
                      </span>
                    )}
                  </p>
                  {doc.description && (
                    <p className="text-xs text-primary-foreground/30 font-body truncate mt-0.5">{doc.description}</p>
                  )}
                  <p className="text-xs text-primary-foreground/20 font-body mt-1">
                    {new Date(doc.created_at).toLocaleDateString()}
                    {versions.length > 0 && (
                      <span className="ml-2 text-primary-foreground/20">· {versions.length} previous version{versions.length > 1 ? "s" : ""}</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(doc.file_path, doc.name)}
                  className="p-2.5 rounded-xl bg-teal/10 text-teal hover:bg-teal/20 transition-all"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>

              {isExpanded && versions.length > 0 && (
                <div className="ml-10 mt-1 space-y-1">
                  {versions.map((v) => (
                    <div key={v.id} className="glass-card rounded-lg p-3 flex items-center gap-3 opacity-70">
                      <Clock className="w-3.5 h-3.5 text-primary-foreground/25 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-primary-foreground/50 font-body truncate">
                          {v.name}
                          {v.version && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-primary-foreground/5 text-[10px]">v{v.version}</span>
                          )}
                        </p>
                        <p className="text-[10px] text-primary-foreground/20 font-body">{new Date(v.created_at).toLocaleDateString()}</p>
                      </div>
                      <button
                        onClick={() => handleDownload(v.file_path, v.name)}
                        className="p-1.5 rounded-lg bg-teal/10 text-teal hover:bg-teal/20 transition-all"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <PortalLayout>
      <div className="mb-8">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Documents</p>
        <h1 className="font-display text-3xl text-primary-foreground tracking-tight mb-6">
          Project <span className="accent-text">Documents</span>
        </h1>

        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-foreground/30" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-11 pr-4 py-3 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40 transition-all"
          />
        </div>
      </div>

      <Tabs defaultValue="project_documents" className="w-full">
        <TabsList className="bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl p-1 mb-6">
          <TabsTrigger
            value="project_documents"
            className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal text-primary-foreground/50 font-body text-sm rounded-lg px-5 py-2"
          >
            Project Documents
          </TabsTrigger>
          <TabsTrigger
            value="voff_digests"
            className="data-[state=active]:bg-teal/20 data-[state=active]:text-teal text-primary-foreground/50 font-body text-sm rounded-lg px-5 py-2"
          >
            VOFF Digests
          </TabsTrigger>
        </TabsList>

        <TabsContent value="project_documents">
          {renderDocList("project_documents")}
        </TabsContent>

        <TabsContent value="voff_digests">
          {renderDocList("voff_digests")}
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default Documents;
